import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reviewsApi, type ReviewsListQuery } from '../api/reviews.api';
import { notifications } from '@mantine/notifications';

export const useReviews = (query: ReviewsListQuery, enabled = true) => {
  return useQuery({
    queryKey: ['reviews', query],
    queryFn: () => reviewsApi.getAll(query),
    enabled,
  });
};

export const useReviewsNewCount = () => {
  return useQuery({
    queryKey: ['reviews', 'new-count'],
    queryFn: () => reviewsApi.getNewCount(),
  });
};

export const useSetReviewApproval = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, approved }: { id: number; approved: boolean }) =>
      reviewsApi.setApproval(id, approved),
    onSuccess: (_, variables,) => {
      // variables содержит аргументы, переданные в mutationFn
      const { approved } = variables;

      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['reviews', 'new-count'] });
      notifications.show({
        title: 'Успешно',
        message: approved ? 'Отзыв подтвержден' : 'Отзыв отклонен',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Ошибка',
        message: error?.response?.data?.message || 'Не удалось подтвердить отзыв',
        color: 'red',
      });
    },
  });
};

export const useDeleteReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => reviewsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['reviews', 'new-count'] });
      notifications.show({
        title: 'Успешно',
        message: 'Отзыв успешно удален',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Ошибка',
        message: error?.response?.data?.message || 'Не удалось удалить отзыв',
        color: 'red',
      });
    },
  });
};

export const useSetSellerReplyApproval = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, approved }: { id: number; approved: boolean }) =>
      reviewsApi.setSellerReplyApproval(id, approved),
    onSuccess: (_, variables) => {
      const { approved } = variables;
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['reviews', 'new-count'] });
      notifications.show({
        title: 'Успешно',
        message: approved
          ? 'Ответ продавца опубликован'
          : 'Ответ продавца отклонён',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Ошибка',
        message:
          error?.response?.data?.message ||
          'Не удалось обновить статус ответа',
        color: 'red',
      });
    },
  });
};

export const useAdminReply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, text }: { id: number; text: string }) =>
      reviewsApi.adminReply(id, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      notifications.show({
        title: 'Успешно',
        message: 'Ответ Лавки цветов опубликован',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Ошибка',
        message:
          error?.response?.data?.message || 'Не удалось отправить ответ',
        color: 'red',
      });
    },
  });
};
